import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import { formatDateTime } from "../../../Helpers/Utils";
import ProfileAvatar from '../../../Assets/images/avatar.png';
import ListLayout from "../../../Components/List";
import Handler from "../../../Helpers/Handler"
import { useAuth } from "../../../Contexts/AuthenticateProvider";
import { TbRosetteDiscountCheckFilled } from 'react-icons/tb';
import { IoMail } from 'react-icons/io5';
import { MdLocalPhone, MdOutlineCancel } from 'react-icons/md';
import { LuCalendarDays } from 'react-icons/lu';
import useListParams from "../../../Helpers/useListParams";

const AssociateList = () => {
    const { hasPermission } = useAuth();
    const canRegister = hasPermission("associate.register");
    const navigate = useNavigate();

    const searchKeys = [
        { key: "associateId", value: "Associate ID" },
        { key: "firstName", value: "Name" },
        { key: "phone", value: "Phone" },
        { key: "email.emailId", value: "Email" },
    ];

    const initialFilters = {
        count: 10,
        sortBy: "createdAt",
        sort: true,
        gender: "all",
        status: "all",
        verified: "all",
        search: "",
        searchKey: "firstName",
    };

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({ list: [], previous: false, next: false });
    // Page number in the URL path (/associates/page/3), filters in the query string - see Helpers/useListParams.
    // (Filters used to live in localStorage, so they leaked into every later visit and new tabs.)
    const { page, setPage, filters, setFilter, resetFilters } = useListParams(initialFilters);

    const setPageNo = (value) => setPage(value);

    const setValue = (name, value) => setFilter(name, value);

    const resetFilter = () => resetFilters();

    const buildPayload = useCallback(() => ({
        sort: filters?.sort,
        sortBy: filters?.sortBy,
        ...(filters?.search ? { filter: filters?.search, filterKey: filters?.searchKey } : {}),
        ...(filters.gender !== undefined && filters?.gender !== "all" ? { gender: filters.gender } : {}),
        ...(filters.status !== undefined && filters?.status !== "all" ? { status: filters.status === "true" || filters.status === true } : {}),
        ...(filters.verified !== undefined && filters?.verified !== "all" ? { verified: filters.verified === "true" || filters.verified === true } : {}),
        ...(filters?.createdFrom ? { createdFrom: new Date(filters?.createdFrom) } : {}),
        ...(filters?.createdTo ? { createdTo: new Date(filters?.createdTo) } : {}),
    }), [filters]);

    const fetchAssociates = useCallback(async () => {
        setLoading(true);
        const response = await Handler({ method: "post", url: `/staff/client/associate/list/${page - 1}`, params: { count: filters.count }, data: buildPayload() });
        if (response?.success) {
            setData({
                list: response?.data?.data?.list || [],
                previous: response?.data?.data?.previous || false,
                next: response?.data?.data?.next || false,
            });
        } else {
            setData({ list: [], previous: false, next: false });
        }
        setLoading(false);
    }, [page, filters.count, buildPayload]);

    const filterItems = {
        dropdown: [
            {
                key: "gender",
                name: "Gender",
                options: [{ key: "all", name: "All" }, { key: "male", name: "Male" }, { key: "female", name: "Female" }, { key: "others", name: "Others" }],
            },
            {
                key: "status",
                name: "Status",
                options: [{ key: "all", name: "All" }, { key: "true", name: "Active" }, { key: "false", name: "Inactive" }],
            },
            {
                key: "verified",
                name: "Verification",
                options: [{ key: "all", name: "All" }, { key: "true", name: "Verified" }, { key: "false", name: "Not verified" }],
            },
        ],
    };

    const tableLayout = useMemo(() => [
        {
            key: "associateId",
            name: "Associate Id",
            mandatory: true,
            render: (row) => <span className="fw-semibold text-dark text-capitalize">{row.associateId || "-"}</span>,
        },
        {
            key: "associate",
            name: "Associate",
            mandatory: true,
            render: (row) => (
                <div className="d-flex align-items-center gap-3">
                    <img src={row.photo || ProfileAvatar} alt={row.firstName} className="rounded-circle border" width={40} height={40} style={{ objectFit: 'cover' }} />
                    <div className="d-flex flex-column">
                        <span className="fw-semibold text-dark text-capitalize">{[row.firstName, row.lastName].filter(Boolean).join(" ") || "Unnamed"}</span>
                        <small className={`d-flex align-items-center gap-1 ${row.verified ? "text-success" : "text-muted"}`}>
                            {row.verified ? <i className="bi bi-patch-check-fill" /> : <i className="bi bi-x-circle" />}
                            {row.verified ? "Verified" : "Unverified"}
                        </small>
                    </div>
                </div>
            ),
        },
        {
            key: "contact",
            name: "Contact Details",
            render: (row) => (
                <div className="small">
                    <div className="text-dark">{row.phone || "No Phone"}</div>
                    <div className="text-muted">{row.email?.emailId || "No Email"}</div>
                </div>
            ),
        },
        { key: "createdAt", name: "Joined Date", render: (row) => <span className="text-muted small">{formatDateTime(row.createdAt)}</span> },
        {
            key: "status",
            name: "Status",
            render: (row) => (
                <span className={`badge rounded-pill ${row.status ? 'text-bg-success' : 'text-bg-danger'}`}>
                    <i className={`bi ${row.status ? 'bi-check-circle' : 'bi-x-circle'}`}></i> {row.status ? "Active" : "Inactive"}
                </span>
            ),
        },
    ], []);

    useEffect(() => {
        fetchAssociates();
    }, [fetchAssociates, filters]);

    const pseudoTotal = (page - 1) * filters.count + data.list.length + (data.next ? filters.count : 0);

    return (
        <ListLayout
            page={page}
            setPageNo={setPageNo}
            pageSize={filters.count}
            onPageSizeChange={(size) => setValue("count", size)}
            title={"Associates"}
            description="Field associates - profile, KYC verification and account status."
            loading={loading}
            onRefresh={fetchAssociates}
            data={{ list: data.list, total: pseudoTotal }}
            table={tableLayout}
            onRowClick={(item) => navigate(`/associates/${item.associateId}`)}
            filterItems={filterItems}
            initialFilters={initialFilters}
            filters={filters}
            onFilterChange={setValue}
            resetFilter={resetFilter}
            searchKeys={searchKeys}
            searchPlaceholder="Search by name, phone, associate ID, or email..."
            addList={canRegister ? () => navigate("/associates/new") : undefined}
        />
    );
};

export default AssociateList;
